import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Check,
  ChevronDown,
  Copy,
  FileText,
  Landmark,
  Loader2,
  UploadCloud,
} from "lucide-react";

import { planCheckoutPaymentReference } from "@fiscalo/shared-utils";

import { cn } from "@/lib/utils";

/** Salkantai's receiving account for plan bank transfers (UYU only). */
const BANK_TRANSFER_ACCOUNT = {
  bank: "Banco Itaú",
  accountType: "Cuenta corriente",
  accountNumber: "2641884",
  currency: "UYU",
  beneficiary: "SALKANTAI SAS",
} as const;

const RECEIPT_ACCEPT = "image/png,image/jpeg,image/webp,application/pdf";
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

// Plan prices in plans.config are NET pesos. IVA is added at checkout
// (PLAN_TAX_RATE_BP = 2200 on the backend) — we display the gross so the
// figure the accountant sees matches what they need to transfer / get charged.
const IVA_RATE = 0.22;

/** Net pesos → gross (IVA-included) pesos, rounded to cents. */
export function grossWithIva(netPesos: number): number {
  return Math.round(netPesos * (1 + IVA_RATE) * 100) / 100;
}

function formatPesosWithCents(amount: number): string {
  return amount.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Annual-plan bank-transfer panel: shows the Salkantai account details, takes
 * the wire receipt (image/PDF), and on submit activates the plan immediately
 * via the parent's onSubmit. Collapsible with a soft expand so it stays out of
 * the way until the accountant opts into it. Shared between the accountant
 * onboarding flow and the generate-payment-link dialog.
 */
export function BankTransferPanel({
  open,
  onToggle,
  onSubmit,
  submitting,
  amountGrossPesos,
  checkoutId,
  billingInterval,
}: {
  open: boolean;
  onToggle: () => void;
  /**
   * Hand the receipt back — together with the checkout it was shown against,
   * so the caller settles the row whose reference the payer actually saw.
   * Null when no checkout could be opened; the caller then falls back to
   * creating one at submit, as every surface used to.
   */
  onSubmit: (
    receipt: File,
    shownCheckoutId: string | null,
  ) => void | Promise<void>;
  submitting: boolean;
  amountGrossPesos: number | null;
  /**
   * The checkout this transfer will settle, from which the payer's reference
   * is derived.
   *
   * THE ID, NOT THE REFERENCE, and REQUIRED rather than optional. Both are
   * deliberate. Deriving here means the string shown and the row settled
   * cannot disagree — the same id goes out through onSubmit — and that is the
   * failure this panel caused for months: three surfaces created their
   * checkout AFTER the receipt, so the payer wired with no reference at all
   * and whatever they wrote in the bank's concepto field read to the verifier
   * as someone else's reference. Required so a new surface must decide rather
   * than silently omit it; null is still allowed, and renders no reference
   * row, for a surface that genuinely has no checkout yet.
   */
  checkoutId: string | null;
  /**
   * REQUIRED, because the activation promise below is not the same for both.
   *
   * Annual activates on upload and verifies afterwards. Monthly is
   * verify-FIRST (spec monthly-bank-transfer): a receipt we look at and doubt
   * HOLDS the activation for human review. Telling a monthly payer their plan
   * is active "al instante" is a promise we may not keep, about money.
   *
   * Not optional with a default: a default would be one of the two promises,
   * and a caller that forgot to pass it would silently make the wrong one.
   */
  billingInterval: 'monthly' | 'annual';
}): JSX.Element {
  const { t } = useTranslation();
  const [receipt, setReceipt] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptFile = (file: File | null | undefined): void => {
    if (!file) return;
    if (file.size > RECEIPT_MAX_BYTES) {
      toast.error(
        t(
          "accountantClientOnboarding.plan.bankTransfer.tooLarge",
          "El comprobante debe pesar menos de 10 MB.",
        ),
      );
      return;
    }
    setReceipt(file);
  };

  const copyAccount = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(BANK_TRANSFER_ACCOUNT.accountNumber);
      toast.success(
        t("accountantClientOnboarding.plan.bankTransfer.copied", "Número copiado."),
      );
    } catch {
      // Clipboard can be blocked; not worth an error toast.
    }
  };

  const rows: Array<{
    label: string;
    value: string;
    copy?: boolean;
    emphasize?: boolean;
  }> = [
    {
      label: t("accountantClientOnboarding.plan.bankTransfer.bank", "Banco"),
      value: BANK_TRANSFER_ACCOUNT.bank,
    },
    {
      label: t(
        "accountantClientOnboarding.plan.bankTransfer.account",
        "Cuenta corriente N°",
      ),
      value: BANK_TRANSFER_ACCOUNT.accountNumber,
      copy: true,
    },
    {
      label: t("accountantClientOnboarding.plan.bankTransfer.currency", "Moneda"),
      value: BANK_TRANSFER_ACCOUNT.currency,
    },
    {
      label: t(
        "accountantClientOnboarding.plan.bankTransfer.beneficiary",
        "Destinatario",
      ),
      value: BANK_TRANSFER_ACCOUNT.beneficiary,
    },
  ];
  const paymentReference =
    checkoutId != null ? planCheckoutPaymentReference(checkoutId) : "";
  if (paymentReference) {
    // LAST, and copyable. It is the one value here that is specific to this
    // payment — everything above is the same on every transfer we receive —
    // so it sits closest to the upload control the payer is heading for.
    rows.push({
      label: t(
        "accountantClientOnboarding.plan.bankTransfer.reference",
        "Referencia",
      ),
      value: paymentReference,
      copy: true,
      emphasize: true,
    });
  }
  if (amountGrossPesos != null) {
    // Insert as second row so it sits right under "Banco" and the accountant
    // can't miss it before they hit their banking app.
    rows.splice(1, 0, {
      label: t(
        "accountantClientOnboarding.plan.bankTransfer.amount",
        "Monto a transferir",
      ),
      value: `UYU ${formatPesosWithCents(amountGrossPesos)}`,
      emphasize: true,
    });
  }

  return (
    <div className={cn("fx-acct-bank", open && "is-open")}>
      <button
        type="button"
        className="fx-acct-bank-toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <Landmark aria-hidden />
        <span>
          {t(
            "accountantClientOnboarding.plan.bankTransfer.toggle",
            "Pagar por transferencia bancaria",
          )}
        </span>
        <ChevronDown className="fx-acct-bank-chevron" aria-hidden />
      </button>

      <div className="fx-acct-bank-collapse">
        <div className="fx-acct-bank-inner">
          <div className="fx-acct-bank-card">
            {rows.map((row) => (
              <div
                className={cn(
                  "fx-acct-bank-row",
                  row.emphasize && "is-emphasized",
                )}
                key={row.label}
              >
                <span className="label">{row.label}</span>
                {row.copy ? (
                  <button
                    type="button"
                    className="value value-copy"
                    onClick={() => void copyAccount()}
                  >
                    {row.value}
                    <Copy aria-hidden />
                  </button>
                ) : (
                  <span className="value">{row.value}</span>
                )}
              </div>
            ))}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={RECEIPT_ACCEPT}
            className="fx-acct-bank-file-input"
            onChange={(e) => {
              acceptFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className={cn(
              "fx-acct-bank-drop",
              dragOver && "is-dragover",
              receipt && "has-file",
            )}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              acceptFile(e.dataTransfer.files?.[0]);
            }}
          >
            {receipt ? (
              <>
                <FileText className="fx-acct-bank-drop-ico" aria-hidden />
                <span className="fx-acct-bank-drop-name">{receipt.name}</span>
                <span className="fx-acct-bank-drop-replace">
                  {t(
                    "accountantClientOnboarding.plan.bankTransfer.replace",
                    "Cambiar archivo",
                  )}
                </span>
              </>
            ) : (
              <>
                <UploadCloud className="fx-acct-bank-drop-ico" aria-hidden />
                <span className="fx-acct-bank-drop-name">
                  {t(
                    "accountantClientOnboarding.plan.bankTransfer.upload",
                    "Subí el comprobante de la transferencia",
                  )}
                </span>
                <span className="fx-acct-bank-drop-hint">
                  {t(
                    "accountantClientOnboarding.plan.bankTransfer.uploadHint",
                    "Imagen o PDF · hasta 10 MB",
                  )}
                </span>
              </>
            )}
          </button>

          <div className="fx-acct-bank-cta">
            <button
              type="button"
              className="fx-acct-section-btn"
              disabled={!receipt || submitting}
              onClick={() => receipt && void onSubmit(receipt, checkoutId)}
            >
              {submitting ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Check aria-hidden />
              )}
              {t(
                "accountantClientOnboarding.plan.bankTransfer.confirm",
                "Ya realicé el pago",
              )}
            </button>
          </div>
          <p className="fx-acct-bank-note">
            {billingInterval === 'annual'
              ? t(
                  "accountantClientOnboarding.plan.bankTransfer.note",
                  "Al subir el comprobante activamos el plan al instante. Verificamos la transferencia luego.",
                )
              : t(
                  "accountantClientOnboarding.plan.bankTransfer.noteMonthly",
                  "Revisamos el comprobante y activamos el plan enseguida. Si algo no coincide, lo mira una persona antes de activar y te avisamos.",
                )}
          </p>
        </div>
      </div>
    </div>
  );
}
